import { create } from 'zustand'

import type { ProductCatalogEntry, ProductMode } from '@fv/client-types'

export type SetProduct = (product: ProductCatalogEntry) => void

export type ProductCatalogInit = {
  filter: ProductCatalogFilter
  setProduct: SetProduct
}

export type ProductCatalogFilter = {
  mode: ProductMode
  onlyContainable?: boolean
}

type ProductCatalogActions = {
  close: () => void
  edit: (handlingUnitId?: string) => void
  open: (init: ProductCatalogInit) => void
  search: (searchText: string) => void
  selectProduct: SetProduct
  setFilter: (filter: ProductCatalogFilter) => void
  showForm: () => void
  showList: () => void
  toggleSelect: (id: string) => void
  selectAll: (ids: string[]) => void
  deselectAll: () => void
}

type ProductCatalogState = {
  editingId?: string
  isOpen: boolean
  filter: ProductCatalogFilter
  searchText: string
  selectedIds: string[]
  setProduct?: SetProduct
  view: 'form' | 'list'
}

const initialState: ProductCatalogState = {
  editingId: undefined,
  isOpen: false,
  filter: {
    mode: 'ltl',
  },
  searchText: '',
  selectedIds: [],
  setProduct: undefined,
  view: 'list',
}

export const useProductCatalog = create<
  ProductCatalogActions & ProductCatalogState
>((set, get) => ({
  ...initialState,
  close: () => set(initialState),
  edit: editingId => {
    set({
      editingId,
      view: editingId === undefined ? 'list' : 'form',
    })
  },
  setFilter: filter => set({ filter }),
  open: ({ filter, setProduct }) => {
    set({
      filter,
      setProduct,
      isOpen: true,
      selectedIds: [],
    })
  },
  search: searchText => set({ searchText, selectedIds: [] }),
  selectProduct: product => {
    get().setProduct?.(product)
    get().close()
  },
  showForm: () => set({ view: 'form' }),
  showList: () => {
    set({
      editingId: undefined,
      view: 'list',
    })
  },
  toggleSelect: id => {
    let selectedIds = get().selectedIds
    if (selectedIds.includes(id)) {
      selectedIds = selectedIds.filter(i => i !== id)
    } else {
      selectedIds = [...selectedIds, id]
    }
    set({ selectedIds })
  },
  selectAll: ids => set({ selectedIds: [...ids] }),
  deselectAll: () => set({ selectedIds: [] }),
}))
